import React from 'react'
import { captureException } from '@sentry/react'
import { InferType } from 'yup'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Modal } from '../../../../../components/common/Modal'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { ModalHeader } from '../../../../../components/common/Modal/ModalHeader'
import { ModalBody } from '../../../../../components/common/Modal/ModalBody'
import { ResetPasswordModalContent } from './ResetPasswordModalContent'
import { useToastNotifications } from '../../../../../hooks/useToastNotification'
import { ToastType } from '../../../../../components/common/Toast/toast-type'
import { adminResetPasswordSchema } from '../../../../../forms/schemas/admin_reset_password'
import { userQueries } from '../../../../../api/users'
import { changeUserPassword } from '../../../../../api/admin'

interface ResetPasswordModalProps {
  show: boolean
  close: () => void
  userId?: string
}

export const ResetPasswordModal = ({
  show,
  close,
  userId,
}: ResetPasswordModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()

  const { data: user, isPending: isLoadingUser } = useQuery(
    userQueries.getUser(userId)
  )

  const {
    mutate: mutateChangeUserPassword,
    isPending: isUpdatingUserPassword,
  } = useMutation({
    mutationFn: changeUserPassword,
    onSuccess: () => {
      close()
      setToastNotification(ToastType.SUCCESS, 'Uppdaterade lösenordet')
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('flashMessage.somethingWentWrongError')
      )
    },
  })
  const onSubmit = async (
    data: InferType<typeof adminResetPasswordSchema>
  ): Promise<void> => {
    user &&
      mutateChangeUserPassword({
        userId: user._id,
        userEmail: user.email,
        newPassword: data.password,
      })
  }

  const onError = (error: unknown): void => {
    captureException(error)
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  return (
    <Modal show={show}>
      {{
        header: <ModalHeader onClose={close}>Ändra lösenord</ModalHeader>,
        body: (
          <ModalBody className="max-h-96 overflow-y-auto">
            <ResetPasswordModalContent
              onError={onError}
              onSubmit={onSubmit}
              onClose={close}
              isLoading={isUpdatingUserPassword || isLoadingUser}
            />
          </ModalBody>
        ),
      }}
    </Modal>
  )
}
